import React from 'react';
import {
  ExpansionPanel,
  ExpansionPanelActionEvent
} from '@progress/kendo-react-layout';
import { SWRConfiguration } from 'swr';
import { useToggle } from 'usehooks-ts';
import cloneDeep from 'lodash.clonedeep';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { TextArea } from '@progress/kendo-react-inputs';
import { Reveal } from '@progress/kendo-react-animation';
import { ValidationRules, validator } from 'nm-validator';
import { Label, Error } from '@progress/kendo-react-labels';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { emailAddress, required } from 'nm-validator/dist/validationRules';

import {
  Event,
  EventFeedback,
  EventPlaylist,
  EventPlaylistFacilitator,
  EventPlaylistLocation,
  GameSettingOverride
} from '../../types/event';
import {
  DeleteEventFeedbackAsync,
  DeleteEventPlaylistAsync,
  DeleteEventPlaylistFacilitatorAsync,
  DeleteEventPlaylistLocationAsync,
  GetEventById,
  GetEventFeedbacks,
  GetEventPlaylistFacilitators,
  GetEventPlaylistLocations,
  GetEventPlaylists,
  PatchEventPlaylistAsync,
  PatchEventPlaylistFacilitatorAsync,
  PatchEventPlaylistLocationAsync,
  PathcEventFeedbackAsync,
  PostEventAsync,
  PostEventFeedbackAsync,
  PostEventPlaylistAsync,
  PostEventPlaylistFacilitatorAsync,
  PostEventPlaylistGameSettingOverride,
  PostEventPlaylistLocationAsync,
  PutEventAsync,
  GetEventPlaylistChallenges,
  PostEventPlaylistChallengeAsync,
  DeleteEventPlaylistChallengeAsync
} from '../../services/events';
import {
  useGetCountries,
  useGetLanguages
} from '../../hooks/services/use-countries';
import { useGetChallenges } from '../../hooks/services/use-challenges';
import { uuid } from '../../types/common-helper';
import { appStore } from '../../stores/app-store';
import HelpSupport from '../../components/help-support';
import { FormInput } from '../../components/form-input';
import { PrivatePage } from '../../components/private-page';
import { FeedbackEvent } from '../../components/feedback-event';
import { ActivityEvent } from '../../components/activity-event';
import { useGetAllUsers } from '../../hooks/services/use-users';
import UserComboBox from '../../components/combobox/user-combobox';
import { useGetTimezones } from '../../hooks/services/use-timezone';
import { useGetPublishedGames } from '../../hooks/services/use-games';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { useGetEnvironments } from '../../hooks/services/use-environments';
import { GetUsersPhoneNumbersAsync } from '../../services/user-phone-number';
import { useGetPublishedFeedbacks } from '../../hooks/services/use-feedbacks';
import GameSettingsDialog, {
  extractBlobResourcesAsync
} from '../../components/dialogs/game-setting-dialog';
import { useGetPublishedAssessments } from '../../hooks/services/use-assessments';
import { ExpansionContentDisabledKeys } from '../../components/expansion-content';
import { Library } from '../../types/library';
import { useToast } from '../../contexts/toast-context';
import { CreateTiloGameAsync } from '../../services/tilo/tilo-proxy';
import { buildDefaultTilConfig } from '../../constants/til-map-data';

interface EventPlaylistMapper {
  eventPlaylist?: EventPlaylist;
  facilitators?: EventPlaylistFacilitator[];
  locations?: EventPlaylistLocation[];
  gameSettingOverride?: GameSettingOverride;
}

interface EventRequestError {
  name?: string;
  clientName?: string;
  clientEmailAddress?: string;
  directorEmailAddress?: string;
  leadFacilitatorEmailAddress?: string;
  salesEmailAddress?: string;
  imageLogoUrl?: string;
}

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }]
};

const swrOptions: SWRConfiguration = {
  revalidateOnFocus: false
};

const EventForm: React.FC<{
  setPageTitle: React.Dispatch<React.SetStateAction<string>>;
}> = React.memo(({ setPageTitle }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isExpand, setIsExpand] = useToggle(false);

  const [event, setEvent] = React.useState<Event>({
    id: 0,
    imageLogoUrl: undefined
  });
  const [eventPlaylists, setEventPlaylists] = React.useState<
    EventPlaylistMapper[]
  >([]);
  const [eventFeedbacks, setEventFeedbacks] = React.useState<EventFeedback[]>(
    []
  );
  const [errors, setErrors] = React.useState<EventRequestError>();
  const [settingOverrideProps, setSettingOverrideProps] = React.useState<{
    playlistIdx: number;
    playlistId: number | undefined;
    gameId: number | undefined;
  }>({
    playlistIdx: -1,
    playlistId: undefined,
    gameId: undefined
  });
  const [populatePlaylist, setPopulatePlaylist] =
    React.useState<EventPlaylistMapper>();

  const { data: users } = useGetAllUsers(undefined, 'organisation', swrOptions);
  const { games } = useGetPublishedGames(
    `${toDataSourceRequestString(initialDataState)}`,
    swrOptions
  );
  const { assessments } = useGetPublishedAssessments(
    `${toDataSourceRequestString(initialDataState)}`,
    swrOptions
  );
  const { data } = useGetEnvironments('', swrOptions);
  const { languages } = useGetLanguages(swrOptions);
  const { countries } = useGetCountries(swrOptions);
  const { timezones } = useGetTimezones(swrOptions);
  const { feedbacks } = useGetPublishedFeedbacks(
    `${toDataSourceRequestString(initialDataState)}`,
    swrOptions
  );
  const { onToastOpen } = useToast();
  const { challenges } = useGetChallenges(swrOptions);
  const languageList = React.useMemo(
    () => languages?.map((l) => l.language ?? ''),
    [languages]
  );

  React.useEffect(() => {
    if (populatePlaylist) {
      if (
        eventPlaylists?.find(
          (x) => x.eventPlaylist?.id === populatePlaylist.eventPlaylist?.id
        ) === undefined
      ) {
        let newPlaylists: EventPlaylistMapper[] = [...eventPlaylists];
        newPlaylists.push(populatePlaylist);
        newPlaylists.sort(function (a, b) {
          return a.eventPlaylist!.id! - b.eventPlaylist!.id!;
        });
        setEventPlaylists(newPlaylists);
      }
    }
  }, [populatePlaylist]);

  const getEventById = async (id: number) => {
    //Get event by id
    appStore.showLoading();
    GetEventById(id)
      .then(async (responseEvent) => {
        // Safeguard: Redirect to view page if event is Completed or already started
        if (
          responseEvent.status === 'Completed' ||
          (responseEvent.startDateUtc &&
            new Date(responseEvent.startDateUtc) <= new Date())
        ) {
          navigate(`/myeventview/${id}`);
          return;
        }

        setPageTitle(responseEvent.name ?? '');
        setEvent(responseEvent);

        //Get event playlists
        Promise.all([GetEventPlaylists(id), GetEventFeedbacks(id)]).then(
          (responseData) => {
            responseData[0]?.data?.forEach(async (playlist) => {
              let newMapper: EventPlaylistMapper = {
                eventPlaylist: playlist
              };

              const isWst =
                playlist.type === 'WST' ||
                playlist.name === "World's Strongest Team";
              const [facilitators, locations, playlistChallenges] =
                await Promise.all([
                  GetEventPlaylistFacilitators(id, playlist.id!),
                  GetEventPlaylistLocations(id, playlist.id!),
                  isWst
                    ? GetEventPlaylistChallenges(id, playlist.id!).then(
                        (res) => res.data
                      )
                    : Promise.resolve([])
                ]);

              newMapper.facilitators = facilitators?.data ?? [];
              newMapper.locations = locations.data ?? [];
              if (playlist) {
                newMapper.eventPlaylist!.challenges = (
                  playlistChallenges ?? []
                ).map((pc) => ({
                  id: pc.challengeId,
                  name: pc.challengeName ?? '',
                  isActive: true
                }));
              }
              setPopulatePlaylist(newMapper);
            });

            setEventFeedbacks(responseData![1]?.data);
          }
        );
      })
      .finally(() => {
        appStore.hideLoading();
      });
  };

  const addNewActivityHandler = () => {
    let newEventPlaylists: EventPlaylistMapper[] = [...eventPlaylists];

    let newPlaylist: EventPlaylistMapper = {
      eventPlaylist: {
        temporaryId: uuid(),
        id: 0,
        eventId: 0,
        status: 'New',
        isGameActivity: false,
        isAssessmentActivity: false,
        name: '',
        environmentId: undefined,
        approxPax: '',
        startDateUtc: undefined,
        endDateUtc: undefined,
        timeZone: '',
        language: '',
        notes: '',
        isDeleted: false,
        imageLogoUrl: ''
      },
      locations: [
        {
          id: 0,
          eventPlaylistId: 0,
          locationCountryISO: '',
          country: '',
          city: '',
          isDeleted: false
        }
      ]
    };

    newEventPlaylists.push(newPlaylist);
    setEventPlaylists(newEventPlaylists);
  };

  const addNewFeedbackHandler = () => {
    let newEventFeedback: EventFeedback[] = [...eventFeedbacks];

    const newData: EventFeedback = {
      id: 0,
      name: '',
      notes: '',
      feedbackId: 0,
      environmentId: 0,
      startDateUtc: undefined,
      endDateUtc: undefined,
      isDeleted: false
    };

    newEventFeedback.push(newData);
    setEventFeedbacks(newEventFeedback);
  };

  const onChange = (field: keyof Event, value: string) => {
    const newEvent = { ...event };
    if (newEvent && field) {
      newEvent[field] = value as never;
    }

    setEvent(newEvent);
  };

  const onGameChanged = (game: EventPlaylist, index: number) => {
    if (game) {
      setEventPlaylists((prev) => {
        const next = [...prev];
        if (next[index]) {
          next[index] = {
            ...next[index],
            eventPlaylist: game
          };
        }
        return next;
      });
    }
  };

  const handleDeleteGame = (index: number) => {
    if (index !== undefined) {
      setEventPlaylists((prev) => {
        const next = [...prev];
        if (next[index]) {
          if (next[index].eventPlaylist!.id! > 0) {
            next[index] = {
              ...next[index],
              eventPlaylist: {
                ...next[index].eventPlaylist!,
                isDeleted: true
              }
            };
          } else {
            next.splice(index, 1);
          }
        }
        return next;
      });
    }
  };

  const handleCopyGame = (index: number) => {
    if (index !== undefined) {
      setEventPlaylists((prev) => {
        const next = [...prev];
        if (next[index]) {
          let copyGame = cloneDeep(next[index]);
          if (copyGame.eventPlaylist) {
            copyGame.eventPlaylist.id = 0;
            copyGame.eventPlaylist.temporaryId = uuid();
          }
          copyGame.facilitators?.forEach((fcl) => {
            fcl.id = 0;
          });
          copyGame.locations?.forEach((loc) => {
            loc.id = 0;
          });
          next.push(copyGame);
        }
        return next;
      });
    }
  };

  const onChangeFacilitatorHandler = (
    index: number,
    facilitator: EventPlaylistFacilitator
  ) => {
    let newEventMapper: EventPlaylistMapper[] = [...eventPlaylists];

    if (newEventMapper[index]?.facilitators === undefined) {
      newEventMapper[index].facilitators = [];
    }

    const facilitatorIndex = newEventMapper[index]?.facilitators?.findIndex(
      (x) => x.facilitatorUserProfileId === facilitator.facilitatorUserProfileId
    );

    if (facilitatorIndex! > -1) {
      if (facilitator.isDeleted) {
        if (facilitator.id === 0) {
          newEventMapper[index].facilitators?.splice(facilitatorIndex!, 1);
        }
        if (facilitator.id! > 0) {
          newEventMapper[index].facilitators![facilitatorIndex!].isDeleted =
            facilitator.isDeleted;
        }
      }
    }

    if (facilitatorIndex === -1 || facilitatorIndex === undefined) {
      newEventMapper[index].facilitators?.push(facilitator);
    }

    setEventPlaylists(newEventMapper);
  };

  const onChangeLocationHandler = (
    index: number,
    locations: EventPlaylistLocation[]
  ) => {
    const newEventMapper: EventPlaylistMapper[] = [...eventPlaylists];
    const selectedEventActivity = newEventMapper[index];

    if (selectedEventActivity.locations === undefined) {
      selectedEventActivity.locations = [];
    }

    selectedEventActivity.locations = locations;
    setEventPlaylists(newEventMapper);
  };

  const onConfirmGameSettingsChanges = React.useCallback(
    async (gameSettingOverride: GameSettingOverride) => {
      setEventPlaylists((prev) => {
        const playlists = cloneDeep(prev);
        playlists[settingOverrideProps.playlistIdx].gameSettingOverride =
          gameSettingOverride;

        return playlists;
      });

      setSettingOverrideProps({
        playlistIdx: -1,
        gameId: undefined,
        playlistId: undefined
      });
    },
    [
      settingOverrideProps.gameId,
      settingOverrideProps.playlistId,
      settingOverrideProps.playlistIdx
    ]
  );

  const onChangeFeedback = (feedback: EventFeedback, index: number) => {
    let newEventFeedback = cloneDeep(eventFeedbacks);
    newEventFeedback[index] = feedback;
    setEventFeedbacks(newEventFeedback);
  };

  const onDeleteFeedback = async (index: number) => {
    if (eventFeedbacks.length > 0 && index >= 0) {
      let newFeedback = cloneDeep(eventFeedbacks);
      if (newFeedback[index].id && newFeedback[index].id! > 0) {
        newFeedback[index].isDeleted = true;
      } else {
        newFeedback = newFeedback.filter(
          (item, indexItem) => indexItem !== index
        );
      }
      setEventFeedbacks(newFeedback);
    }
  };

  const onCopyFeedback = (index: number) => {
    if (eventFeedbacks.length > 0 && index >= 0) {
      let newFeedback = cloneDeep(eventFeedbacks[index]);
      newFeedback.id = 0;
      let newFeedbacks = cloneDeep(eventFeedbacks);
      newFeedbacks?.push(newFeedback);
      setEventFeedbacks(newFeedbacks);
    }
  };

  const getPhoneNumber = async (userId: number) => {
    const response = await GetUsersPhoneNumbersAsync(userId);
    const phoneNumber = response?.data.find((o) => o.type === 'Work');

    if (!phoneNumber) return '';
    return (
      (phoneNumber.countryCode ? `+${phoneNumber.countryCode}` : '') +
      phoneNumber.number
    );
  };

  const onChangeUserEvent = async (
    changeEvent: ComboBoxChangeEvent,
    userCategory: string
  ) => {
    let userProfileId: number | undefined = undefined;
    let userEmailAddress: string | undefined = undefined;
    let userFullName: string | undefined = undefined;
    let phoneNumber: string = '';
    if (changeEvent.value === null) {
      userProfileId = undefined;
      userEmailAddress = '';
      userFullName = '';
    } else {
      phoneNumber = await getPhoneNumber(changeEvent.value.userId);
      userProfileId = changeEvent.value?.userId;
      userEmailAddress = changeEvent.value?.username;
      userFullName = changeEvent.value?.fullName;
    }
    if (userCategory === 'salesPerson') {
      setEvent({
        ...event,
        salesName: userFullName!,
        salesUserProfileId: userProfileId!,
        salesEmailAddress: userEmailAddress!,
        salesPhoneNumber: phoneNumber
      });
    }

    if (userCategory === 'director') {
      setEvent({
        ...event,
        directorName: userFullName!,
        directorUserProfileId: userProfileId!,
        directorEmailAddress: userEmailAddress!,
        directorPhoneNumber: phoneNumber
      });
    }

    if (userCategory === 'facilitator') {
      setEvent({
        ...event,
        leadFacilitatorName: userFullName!,
        leadFacilitatorUserProfileId: userProfileId!,
        leadFacilitatorEmailAddress: userEmailAddress!,
        leadFacilitatorPhoneNumber: phoneNumber
      });
    }
  };

  const PostEventPlaylistFacilitator = (
    eventId: number,
    playlistId: number,
    facilitator: EventPlaylistFacilitator
  ) => {
    PostEventPlaylistFacilitatorAsync(eventId!, playlistId!, facilitator);
  };

  const PostEventPlaylistLocation = (
    eventId: number,
    playlistId: number,
    location: EventPlaylistLocation
  ) => {
    PostEventPlaylistLocationAsync(eventId!, playlistId!, location);
  };

  const PostEventPlaylist = async (
    eventId: number,
    eventPlaylist: EventPlaylist,
    facilitators?: EventPlaylistFacilitator[],
    locations?: EventPlaylistLocation[]
  ) => {
    const isWstVal =
      eventPlaylist.type === 'WST' ||
      eventPlaylist.name === "World's Strongest Team";
    if (isWstVal && eventPlaylist.startDateUtc && !eventPlaylist.endDateUtc) {
      eventPlaylist.endDateUtc = eventPlaylist.startDateUtc;
    }

    const playlistPayload = { ...eventPlaylist };
    if (isWstVal || playlistPayload.type === 'TIL' || playlistPayload.type === 'SPA') {
      playlistPayload.environmentId = undefined;
    }

    return await PostEventPlaylistAsync(eventId, playlistPayload!).then(
      async (playlistResponse) => {
        facilitators?.forEach((facilitator) => {
          //Insert a new facilitator
          PostEventPlaylistFacilitator(
            eventId,
            playlistResponse.id!,
            facilitator
          );
        });
        //Insert a new locaiton
        locations?.forEach((location) => {
          PostEventPlaylistLocation(eventId, playlistResponse.id!, location);
        });

        // Save WST challenges
        if (
          isWstVal &&
          eventPlaylist.challenges &&
          eventPlaylist.challenges.length > 0
        ) {
          await Promise.all(
            eventPlaylist.challenges.map((c) =>
              PostEventPlaylistChallengeAsync(eventId, playlistResponse.id!, {
                challengeId: c.id
              })
            )
          );
        }

        return playlistResponse;
      }
    );
  };

  const addFeedbackGames = (eventPlaylistsResponse: EventPlaylist) => {
    const feedbackGames = {
      code: eventPlaylistsResponse.code!,
      name: eventPlaylistsResponse.name!
    };

    let newEventFeedback = cloneDeep(
      eventFeedbacks.map((item) => {
        let newItem = item;
        if (newItem.games === undefined) {
          newItem.games = [];
        }
        const isExist = newItem.games.find(
          (i) => i.code === feedbackGames.code
        );
        if (!isExist) {
          newItem.games.push(feedbackGames);
        }
        return newItem;
      })
    );
    setEventFeedbacks(newEventFeedback);
  };

  const toastrEventSave = (isError: boolean) => {
    if (isError) {
      onToastOpen({
        title: 'My events',
        body: <div>Failed to save event.</div>,
        style: 'error'
      });
    } else {
      onToastOpen({
        title: 'My events',
        body: <div>Event saved.</div>,
        style: 'success'
      });
    }
  };

  const addEventHandler = async () => {
    appStore.showLoading();
    event.type = 'Game';
    event.status = 'New';

    // Create all TILO games before creating the event — if any fail, abort without touching the DB
    for (const playlist of eventPlaylists) {
      const pl = playlist.eventPlaylist!;
      if (pl.type === 'TILO') {
        try {
          const tiloGame = await CreateTiloGameAsync({
            name: event.name ?? '',
            client: event.clientName ?? '',
            gameType: pl.tiloGameType ?? '',
            nbrDoors: pl.tiloNbrDoors ?? 0,
            hideScore: pl.tiloHideScore ?? false,
            playtime: pl.tiloPlaytime ?? 0,
            startsAt: new Date(pl.startDateUtc!).toUTCString(),
            language: pl.language ?? '',
            teamsCount: pl.tiloTeamsCount ?? 0,
            nbrPlayersPerTeam: pl.tiloNbrPlayersPerTeam ?? 0,
            locationCountryISO: playlist.locations?.find((l) => !l.isDeleted)
              ?.locationCountryISO
          });
          pl.tiloGameId = tiloGame.GAME._id;
        } catch {
          onToastOpen({
            title: 'TILO',
            body: <div>Failed to create TILO game. Event was not saved.</div>,
            style: 'error'
          });
          appStore.hideLoading();
          return;
        }
      }
      if (pl.type === 'TIL') {
        pl.tilIsSynced = false;
        if (!pl.tilTemplateConfig) {
          pl.tilTemplateConfig = JSON.stringify(buildDefaultTilConfig());
        }
      }
    }

    //Insert a new event
    await PostEventAsync(event)
      .then(async (response) => {
        // insert a new playlist and feedback handler
        for (let i = 0; i < eventPlaylists.length; i++) {
          const playlist = eventPlaylists[i];
          let newPlaylist = playlist.eventPlaylist!;
          newPlaylist.eventId = response.id;
          newPlaylist.status = 'New';
          newPlaylist.imageLogoUrl = response.imageLogoUrl;

          if (newPlaylist.type === 'SPA') {
            newPlaylist.name = `${event.name ?? ''} - Activity ${i + 1}`;
          }

          const responsePlaylist = await PostEventPlaylist(
            response.id!,
            newPlaylist,
            playlist.facilitators ?? [],
            playlist.locations ?? []
          );

          if (
            responsePlaylist.id &&
            playlist.eventPlaylist?.gameId &&
            playlist.gameSettingOverride
          ) {
            const gameSettingOverride = await extractBlobResourcesAsync(
              playlist.eventPlaylist.gameId.toString(),
              playlist.gameSettingOverride
            );

            const override = await PostEventPlaylistGameSettingOverride(
              response.id!,
              responsePlaylist.id,
              gameSettingOverride
            );
          }

          // check if any feedback defined
          if (
            eventFeedbacks.filter((item) => item.isDeleted === false).length > 0
          ) {
            if (responsePlaylist && responsePlaylist.code) {
              // add games into feedback state
              addFeedbackGames(responsePlaylist);
            }
          }
        }

        // insert new feedback
        eventFeedbacks
          .filter((item) => item.isDeleted === false)
          .forEach(async (feedbackItem) => {
            await PostEventFeedbackAsync(response.id!, feedbackItem);
          });

        toastrEventSave(false);
        appStore.hideLoading();
        navigate('/myevents');
      })
      .catch((ex) => {
        console.error(ex);
      })
      .finally(() => {
        appStore.hideLoading();
      });
  };

  const PatchEventPlaylist = async (
    eventId: number,
    eventPlaylist: EventPlaylist,
    facilitators?: EventPlaylistFacilitator[],
    locations?: EventPlaylistLocation[]
  ) => {
    const isWstVal =
      eventPlaylist.type === 'WST' ||
      eventPlaylist.name === "World's Strongest Team";
    if (isWstVal && eventPlaylist.startDateUtc && !eventPlaylist.endDateUtc) {
      eventPlaylist.endDateUtc = eventPlaylist.startDateUtc;
    }

    const playlistPayload = { ...eventPlaylist };
    if (isWstVal || playlistPayload.type === 'TIL' || playlistPayload.type === 'SPA') {
      playlistPayload.environmentId = undefined;
    }

    return await PatchEventPlaylistAsync(
      eventId,
      playlistPayload.id!,
      playlistPayload!
    ).then(async (playlistResponse) => {
      facilitators?.forEach((facilitator) => {
        if (facilitator.isDeleted) {
          DeleteEventPlaylistFacilitatorAsync(
            eventId,
            eventPlaylist.id!,
            facilitator.id!,
            facilitator
          );
        }
        if (!facilitator.isDeleted) {
          if (facilitator.id === 0) {
            //Insert a new facilitator
            PostEventPlaylistFacilitator(
              eventId,
              playlistResponse.id!,
              facilitator
            );
          }
          if (facilitator.id! > 0) {
            //Insert a new facilitator
            PatchEventPlaylistFacilitatorAsync(
              eventId,
              playlistResponse.id!,
              facilitator.id!,
              facilitator
            );
          }
        }
      });

      locations?.forEach((location) => {
        if (location.isDeleted) {
          DeleteEventPlaylistLocationAsync(
            eventId,
            eventPlaylist.id!,
            location.id!,
            location
          );
        }
        if (!location.isDeleted) {
          if (location.id === 0) {
            //Insert a new location
            PostEventPlaylistLocation(eventId, playlistResponse.id!, location);
          }
          if (location.id > 0) {
            //Insert a new location
            PatchEventPlaylistLocationAsync(
              eventId,
              playlistResponse.id!,
              location.id!,
              location
            );
          }
        }
      });

      // Update challenges if WST
      if (isWstVal) {
        const existingRes = await GetEventPlaylistChallenges(
          eventId,
          eventPlaylist.id!
        );
        const existing = existingRes?.data ?? [];
        const newChallenges = eventPlaylist.challenges ?? [];

        const toDelete = existing.filter(
          (ec) => !newChallenges.some((nc) => nc.id === ec.challengeId)
        );
        const toAdd = newChallenges.filter(
          (nc) => !existing.some((ec) => ec.challengeId === nc.id)
        );

        await Promise.all([
          ...toDelete.map((c) =>
            DeleteEventPlaylistChallengeAsync(
              eventId,
              eventPlaylist.id!,
              c.challengeId
            )
          ),
          ...toAdd.map((c) =>
            PostEventPlaylistChallengeAsync(eventId, eventPlaylist.id!, {
              challengeId: c.id
            })
          )
        ]);
      }

      return playlistResponse;
    });
  };

  const editEventHandler = async (eventId: number) => {
    if (eventId) {
      appStore.showLoading();

      // Create TILO games for any new TILO playlists before saving
      for (const playlist of eventPlaylists) {
        const pl = playlist.eventPlaylist!;
        if (pl.id === 0 && pl.type === 'TILO') {
          try {
            const tiloGame = await CreateTiloGameAsync({
              name: event.name ?? '',
              client: event.clientName ?? '',
              gameType: pl.tiloGameType ?? '',
              nbrDoors: pl.tiloNbrDoors ?? 0,
              hideScore: pl.tiloHideScore ?? false,
              playtime: pl.tiloPlaytime ?? 0,
              startsAt: new Date(pl.startDateUtc!).toUTCString(),
              language: pl.language ?? '',
              teamsCount: pl.tiloTeamsCount ?? 0,
              nbrPlayersPerTeam: pl.tiloNbrPlayersPerTeam ?? 0,
              locationCountryISO: playlist.locations?.find((l) => !l.isDeleted)
                ?.locationCountryISO
            });
            pl.tiloGameId = tiloGame.GAME._id;
          } catch {
            onToastOpen({
              title: 'TILO',
              body: <div>Failed to create TILO game. Event was not saved.</div>,
              style: 'error'
            });
            appStore.hideLoading();
            return;
          }
        }
        if (pl.type === 'TIL') {
          if (pl.id === 0) {
            pl.tilIsSynced = false;
          }
          if (!pl.tilTemplateConfig) {
            pl.tilTemplateConfig = JSON.stringify(buildDefaultTilConfig());
          }
        }
      }

      const newEvent = cloneDeep(event);
      await PutEventAsync(eventId, newEvent)
        .then((response) => {
          toastrEventSave(false);

          // event playlist handler
          eventPlaylists.forEach(async (playlist, i) => {
            let newPlaylist = playlist.eventPlaylist!;
            newPlaylist.eventId = response.id;
            newPlaylist.status = 'New';
            newPlaylist.imageLogoUrl = response.imageLogoUrl;

            if (newPlaylist.type === 'SPA') {
              newPlaylist.name = `${event.name ?? ''} - Activity ${i + 1}`;
            }

            if (playlist.eventPlaylist?.isDeleted) {
              DeleteEventPlaylistAsync(eventId, playlist.eventPlaylist.id!);
            }

            if (!playlist.eventPlaylist?.isDeleted) {
              const sendGameSettingOverride = async (
                playlistId: number,
                gameId: number,
                setting: GameSettingOverride
              ) => {
                const gameSettingOverride = await extractBlobResourcesAsync(
                  gameId.toString(),
                  setting
                );

                const override = await PostEventPlaylistGameSettingOverride(
                  response.id!,
                  playlistId,
                  gameSettingOverride
                );
              };

              if (playlist.eventPlaylist?.id === 0) {
                const responsePlaylist = await PostEventPlaylist(
                  eventId,
                  playlist.eventPlaylist,
                  playlist.facilitators ?? [],
                  playlist.locations ?? []
                );
                if (responsePlaylist && responsePlaylist.code) {
                  //add games into feedback state
                  addFeedbackGames(responsePlaylist);
                  if (responsePlaylist.gameId && playlist.gameSettingOverride)
                    await sendGameSettingOverride(
                      responsePlaylist.id!,
                      responsePlaylist.gameId,
                      playlist.gameSettingOverride
                    );
                }
              }

              if (playlist.eventPlaylist!.id! > 0) {
                const responsePlaylist = await PatchEventPlaylist(
                  eventId,
                  playlist.eventPlaylist!,
                  playlist.facilitators ?? [],
                  playlist.locations ?? []
                );
                if (responsePlaylist && responsePlaylist.code) {
                  //add games into feedback state
                  addFeedbackGames(responsePlaylist);
                  if (responsePlaylist.gameId && playlist.gameSettingOverride)
                    await sendGameSettingOverride(
                      responsePlaylist.id!,
                      responsePlaylist.gameId,
                      playlist.gameSettingOverride
                    );
                }
              }
            }
          });

          // event feedback handler
          eventFeedbacks.forEach(async (feedbackItem) => {
            // delete feedback
            if (feedbackItem.isDeleted) {
              await DeleteEventFeedbackAsync(response.id!, feedbackItem.id!);
            }

            // patch feedback
            if (feedbackItem.isDeleted === false && feedbackItem.id! > 0) {
              await PathcEventFeedbackAsync(
                response.id!,
                feedbackItem.id!,
                feedbackItem
              );
            }

            // add feedback
            if (feedbackItem.isDeleted === false && feedbackItem.id! === 0) {
              await PostEventFeedbackAsync(response.id!, feedbackItem);
            }
          });
        })
        .finally(() => appStore.hideLoading());

      navigate('/myevents');
    }
  };

  const validate = () => {
    const rules: ValidationRules<Event> = {
      name: [required()],
      clientName: [required()]
    };

    let errorEmail = 'Please enter a valid email address';

    if (event.clientEmailAddress && event.clientEmailAddress !== '') {
      rules.clientEmailAddress = [emailAddress(errorEmail)];
    }

    if (event.directorEmailAddress && event.directorEmailAddress !== '') {
      rules.directorEmailAddress = [emailAddress(errorEmail)];
    }

    if (
      event.leadFacilitatorEmailAddress &&
      event.leadFacilitatorEmailAddress !== ''
    ) {
      rules.leadFacilitatorEmailAddress = [emailAddress(errorEmail)];
    }

    if (!event.clientName) {
      let error = 'Client is required.';
      onToastOpen({
        title: 'Error',
        body: <div>{error}</div>,
        style: 'error'
      });
    }

    if (!event.name) {
      let error = 'Event title is required.';
      onToastOpen({
        title: 'Error',
        body: <div>{error}</div>,
        style: 'error'
      });
    }

    var validationResult = validator.validateObject<Event>(event, rules);
    setErrors(validationResult.errors);
    return validationResult;
  };

  const isValidationActivity = (eventPlaylists: EventPlaylistMapper[]) => {
    let counterFalse = 0;
    eventPlaylists.forEach((event) => {
      if (event.eventPlaylist?.isDeleted) return;
      if (!event.eventPlaylist?.gameId && !event.eventPlaylist?.assessmentId) {
        counterFalse++;
      }
    });
    return counterFalse > 0 ? false : true;
  };

  const onSubmitHandler = async () => {
    // If start date reached, do not allow editing / saving (only for WST games)
    const activePlaylists = eventPlaylists.filter(
      (p) => !p.eventPlaylist?.isDeleted
    );
    const hasWst = activePlaylists.some(
      (p) =>
        p.eventPlaylist?.type === 'WST' ||
        p.eventPlaylist?.name === "World's Strongest Team"
    );

    if (hasWst) {
      const startDates = activePlaylists
        .map((p) => p.eventPlaylist?.startDateUtc)
        .filter((d): d is Date => !!d)
        .map((d) => new Date(d));
      const earliestStartDate =
        startDates.length > 0
          ? new Date(Math.min(...startDates.map((d) => d.getTime())))
          : undefined;

      if (id && earliestStartDate && earliestStartDate <= new Date()) {
        onToastOpen({
          title: 'Error',
          body: <div>The event has already started and cannot be edited.</div>,
          style: 'error'
        });
        return;
      }
    }

    const validationResult = validate();
    if (!validationResult.isValid) return;

    const activeFeedbacks = eventFeedbacks.filter((f) => !f.isDeleted);

    // activity validation, at least we have an activity or a feedback
    if (activePlaylists.length === 0 && activeFeedbacks.length === 0) {
      onToastOpen({
        title: 'Error',
        body: <div>Activity or Feedback is required.</div>,
        style: 'error'
      });

      return;
    }

    // there is activity, validate it
    if (activePlaylists.length > 0) {
      if (!isValidationActivity(eventPlaylists)) {
        onToastOpen({
          title: 'Error',
          body: <div>Game or Assessment is required.</div>,
          style: 'error'
        });

        return;
      } else {
        let isPlaylistValid = true;
        eventPlaylists.forEach((f) => {
          if (f.eventPlaylist?.isDeleted) return;
          const isWst =
            f.eventPlaylist?.type === 'WST' ||
            f.eventPlaylist?.name === "World's Strongest Team";
          if (isWst) {
            if (!f.eventPlaylist?.startDateUtc) {
              isPlaylistValid = false;
              return;
            }
            if (!f.eventPlaylist?.language) {
              isPlaylistValid = false;
              return;
            }
            if (!f.eventPlaylist?.approxPax) {
              isPlaylistValid = false;
              return;
            }
            if (
              f.locations?.length === 0 ||
              f.locations?.some(
                (location) => !location.country || !location.city
              )
            ) {
              isPlaylistValid = false;
              return;
            }
            if (
              !f.eventPlaylist?.challenges ||
              f.eventPlaylist.challenges.length === 0
            ) {
              isPlaylistValid = false;
              return;
            }
            return;
          }

          if (f.eventPlaylist?.type === 'TIL') {
            if (
              !f.eventPlaylist.tiloTeamsCount ||
              !f.eventPlaylist.tiloPlaytime
            ) {
              isPlaylistValid = false;
            }
            return;
          }
          if (f.eventPlaylist?.type === 'SPA') {
            if (!f.eventPlaylist?.startDateUtc) {
              isPlaylistValid = false;
              return;
            }
            if (!f.eventPlaylist?.endDateUtc) {
              isPlaylistValid = false;
              return;
            }
            if (!f.eventPlaylist?.language) {
              isPlaylistValid = false;
              return;
            }
            if (
              f.locations?.length === 0 ||
              f.locations?.some((location) => !location.country || !location.city)
            ) {
              isPlaylistValid = false;
              return;
            }
            return;
          }
          if (!f.eventPlaylist?.language) {
            isPlaylistValid = false;
            return;
          }
          if (!f.eventPlaylist?.approxPax && f.eventPlaylist?.type !== 'TILO') {
            isPlaylistValid = false;
            return;
          }
          if (f.eventPlaylist?.type === 'TILO') {
            if (
              !f.eventPlaylist.tiloNbrDoors ||
              !f.eventPlaylist.tiloGameType ||
              !f.eventPlaylist.tiloTeamsCount ||
              !f.eventPlaylist.tiloNbrPlayersPerTeam ||
              !f.eventPlaylist.tiloPlaytime
            ) {
              isPlaylistValid = false;
              return;
            }
          }
          if (!f.eventPlaylist?.startDateUtc) {
            isPlaylistValid = false;
            return;
          }
          if (!f.eventPlaylist?.endDateUtc) {
            isPlaylistValid = false;
            return;
          }
          if (f.eventPlaylist?.environmentId === undefined) {
            isPlaylistValid = false;
            return;
          }
          if (
            f.locations?.length === 0 ||
            f.locations?.some((location) => !location.country || !location.city)
          ) {
            isPlaylistValid = false;
            return;
          }
        });
        if (!isPlaylistValid) return;
      }
    }

    // there is feedback, validate it
    if (activeFeedbacks.length > 0) {
      const getFeedbacksInValid = eventFeedbacks.find(
        (item) => !item.isDeleted && (!item.feedbackId || item.feedbackId === 0)
      );
      if (getFeedbacksInValid) return;

      let isFeedbackValid = true;
      eventFeedbacks.forEach((f) => {
        if (f.isDeleted) return;
        if (!f?.startDateUtc) {
          isFeedbackValid = false;
          return;
        }
        if (!f?.endDateUtc) {
          isFeedbackValid = false;
          return;
        }
      });
      if (!isFeedbackValid) return;
    }

    if (id) {
      await editEventHandler(parseInt(id!));
    } else {
      addEventHandler();
    }
  };

  const reorderGamesByType = (data: Library[]) => {
    const nonGames = data.filter((item) => item.type !== 'Game');
    const games = data.filter((item) => item.type === 'Game');

    if (games.length > 0) {
      games[0].isGameDivider = true;
    }

    return [...nonGames, ...games];
  };

  React.useEffect(() => {
    if (id) {
      getEventById(parseInt(id));
    } else {
      setPageTitle('New Event');
    }
  }, [id]);

  return (
    <>
      <div className={'container d-flex flex-column gap-4'}>
        <div className={'card p-4'}>
          <div className={'p-1'}>
            <FormInput
              id={'title'}
              label={'Event name'}
              value={event.name}
              onChange={(e) => {
                onChange('name', e.value);
                if (id) setPageTitle(e.value);
              }}
            />
            {errors?.name && <Error>{errors?.name}</Error>}
          </div>
        </div>

        {eventPlaylists.map((activity, index) => {
          if (activity.eventPlaylist?.isDeleted) return null;
          return (
            <ActivityEvent
              key={
                activity.eventPlaylist?.id ||
                activity.eventPlaylist?.temporaryId
              }
              index={index}
              users={users?.data ?? []}
              libraries={reorderGamesByType(games)}
              timezones={timezones}
              countries={countries}
              languages={languageList}
              assessments={assessments}
              environments={data?.data ?? []}
              game={activity.eventPlaylist}
              locations={activity?.locations ?? []}
              facilitators={activity?.facilitators ?? []}
              challenges={challenges}
              onChange={onGameChanged}
              onCopyGame={handleCopyGame}
              onDeleteGame={handleDeleteGame}
              onChangeFacilitator={(fc) => {
                onChangeFacilitatorHandler(index, fc);
              }}
              onChangeLocation={(locations) => {
                onChangeLocationHandler(index, locations);
              }}
              onChangeGameSettings={(gameId: number) => {
                setSettingOverrideProps({
                  playlistIdx: index,
                  playlistId: activity.eventPlaylist?.id,
                  gameId
                });
              }}
            />
          );
        })}

        {/*
          Multiple TIL activities are permitted per event.
          Ref: https://dev.azure.com/catalyst-global/TIL%20The%20Infinite%20Loop/_workitems/edit/11620
        */}
        <div
          className={'card p-4 bg-light text-decoration-none cursor-pointer'}
          onClick={addNewActivityHandler}>
          <div
            className={
              'd-flex p-1 align-items-center justify-content-center'
            }>
            <span className={'material-symbols-outlined fw-bold p-2'}>
              event
            </span>
            <span className={'fw-bold p-2 '}>Add Activity</span>
          </div>
        </div>

        {eventFeedbacks.map((item, index) => (
          <FeedbackEvent
            key={index}
            index={index}
            feedback={item}
            feedbackList={feedbacks}
            environments={data?.data ?? []}
            onChange={onChangeFeedback}
            onCopyFeedback={onCopyFeedback}
            onDeleteFeedback={onDeleteFeedback}
          />
        ))}

        <div
          className={'card p-4 bg-light cursor-pointer text-decoration-none'}
          onClick={addNewFeedbackHandler}>
          <div
            className={'d-flex p-1 align-items-center justify-content-center'}>
            <span className={'material-symbols-outlined fw-bold p-2'}>
              insert_chart
            </span>
            <span className={'fw-bold p-2 '}>Add Feedback</span>
          </div>
        </div>

        <div>
          <span>Client/Agent</span>
          <div className={'card p-4'}>
            <div className={'row p-1'}>
              <div className={'col-md-6'}>
                <FormInput
                  id="client"
                  label={'Client*'}
                  value={event.clientName}
                  onChange={(e) => onChange('clientName', e.value)}
                />
                {errors?.clientName && <Error>{errors?.clientName}</Error>}
              </div>
              <div className={'col-md-6'}>
                <FormInput
                  label={'Agent'}
                  value={event.agency}
                  onChange={(e) => onChange('agency', e.value)}
                />
              </div>
            </div>

            <div className={'row p-1'}>
              <div className={'col-md-6'}>
                <FormInput
                  label={'Contact name'}
                  value={event.clientContact}
                  onChange={(e) => onChange('clientContact', e.value)}
                />
              </div>
              <div className={'col-md-6'}>
                <FormInput
                  label={'Phone number'}
                  value={event.clientPhoneNumber}
                  onChange={(e) => onChange('clientPhoneNumber', e.value)}
                />
              </div>
            </div>

            <div className={'row p-1'}>
              <div className={'col-md-6'}>
                <FormInput
                  type={'email'}
                  label={'Client email address'}
                  value={event.clientEmailAddress}
                  onChange={(e) => onChange('clientEmailAddress', e.value)}
                />
                {errors?.clientEmailAddress && (
                  <Error>{errors?.clientEmailAddress}</Error>
                )}
              </div>
              <div className={'col-md-6'}>
                <FormInput
                  type={'text'}
                  label={'Industry'}
                  value={event.industry}
                  onChange={(e) => onChange('industry', e.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <ExpansionPanel
          title={'Staffing'}
          expanded={isExpand}
          onAction={(event: ExpansionPanelActionEvent) => setIsExpand()}>
          <Reveal>
            {isExpand && (
              <ExpansionContentDisabledKeys>
                <div>Sales person</div>
                <div className={'card p-4'}>
                  <div className={'row p-1'}>
                    <div className={'col-md-4'}>
                      <Label>Name</Label>
                      <UserComboBox
                        popupSettings={{
                          animate: false
                        }}
                        clearButton={true}
                        value={{
                          id: event?.salesUserProfileId,
                          fullName: event?.salesName
                        }}
                        onChange={(e) => onChangeUserEvent(e, 'salesPerson')}
                      />
                    </div>
                    <div className={'col-md-4'}>
                      <FormInput
                        label={'Phone number'}
                        value={event.salesPhoneNumber}
                        onChange={(e) => onChange('salesPhoneNumber', e.value)}
                      />
                    </div>
                  </div>
                  <div className={'row p-1'}>
                    <div className={'col-md-4'}>&nbsp;</div>
                    <div className={'col-md-8'}>
                      <FormInput
                        type={'email'}
                        label={'Email address'}
                        value={event.salesEmailAddress}
                        onChange={(e) => onChange('salesEmailAddress', e.value)}
                      />
                      {errors?.salesEmailAddress && (
                        <Error>{errors?.salesEmailAddress}</Error>
                      )}
                    </div>
                  </div>
                </div>

                <div className={'mt-4'}>Lead facilitator</div>
                <div className={'card p-4'}>
                  <div className={'row p-1'}>
                    <div className={'col-md-4'}>
                      <Label>Facilitator name</Label>
                      <UserComboBox
                        clearButton={true}
                        popupSettings={{
                          animate: false
                        }}
                        value={{
                          id: event?.leadFacilitatorUserProfileId,
                          fullName: event?.leadFacilitatorName
                        }}
                        onChange={(e) => onChangeUserEvent(e, 'facilitator')}
                      />
                    </div>
                    <div className={'col-md-4'}>
                      <FormInput
                        label={'Facilitator phone number'}
                        value={event.leadFacilitatorPhoneNumber}
                        onChange={(e) =>
                          onChange('leadFacilitatorPhoneNumber', e.value)
                        }
                      />
                    </div>
                  </div>
                  <div className={'row p-1'}>
                    <div className={'col-md-4'}>&nbsp;</div>
                    <div className={'col-md-8'}>
                      <FormInput
                        type={'email'}
                        label={'Facilitator email address'}
                        value={event.leadFacilitatorEmailAddress}
                        onChange={(e) =>
                          onChange('leadFacilitatorEmailAddress', e.value)
                        }
                      />
                      {errors?.leadFacilitatorEmailAddress && (
                        <Error>{errors?.leadFacilitatorEmailAddress}</Error>
                      )}
                    </div>
                  </div>
                </div>

                <div className={'mt-4'}>Event director</div>
                <div className={'card p-4'}>
                  <div className={'row p-1'}>
                    <div className={'col-md-4'}>
                      <Label>Director name</Label>
                      <UserComboBox
                        popupSettings={{
                          animate: false
                        }}
                        clearButton={true}
                        value={{
                          id: event?.directorUserProfileId,
                          fullName: event?.directorName
                        }}
                        onChange={(e) => onChangeUserEvent(e, 'director')}
                      />
                    </div>

                    <div className={'col-md-4'}>
                      <FormInput
                        label={'Director phone number'}
                        value={event.directorPhoneNumber}
                        onChange={(e) =>
                          onChange('directorPhoneNumber', e.value)
                        }
                      />
                    </div>
                  </div>

                  <div className={'row p-1'}>
                    <div className={'col-md-4'}>&nbsp;</div>
                    <div className={'col-md-8'}>
                      <FormInput
                        type={'email'}
                        label={'Director email address'}
                        value={event.directorEmailAddress}
                        onChange={(e) =>
                          onChange('directorEmailAddress', e.value)
                        }
                      />
                      {errors?.directorEmailAddress && (
                        <Error>{errors?.directorEmailAddress}</Error>
                      )}
                    </div>
                  </div>
                </div>
              </ExpansionContentDisabledKeys>
            )}
          </Reveal>
        </ExpansionPanel>

        <div className={'p-1'}>
          <Label>Event notes</Label>
          <TextArea
            rows={10}
            maxLength={1000}
            value={event?.notes}
            onChange={(e) => onChange('notes', e.value)}
          />
          <div className="d-flex justify-content-end mt-1">
            <span className="text-muted fs-8">
              {event?.notes?.length ?? 0} / 1000
            </span>
          </div>
        </div>

        <div>
          <hr />
          <div className={'d-flex justify-content-end align-items-center'}>
            <Button
              themeColor={'secondary'}
              className={'w-10 text-light'}
              onClick={() => navigate('/myevents')}>
              Cancel
            </Button>

            <Button
              themeColor={'primary'}
              className={'w-10 text-light ml-2'}
              onClick={onSubmitHandler}>
              Save
            </Button>
          </div>
        </div>
      </div>

      {settingOverrideProps.playlistIdx !== -1 &&
        settingOverrideProps.gameId !== undefined && (
          <GameSettingsDialog
            eventName={event.name ?? ''}
            settingOverride={
              eventPlaylists[settingOverrideProps.playlistIdx]
                .gameSettingOverride
            }
            gameId={settingOverrideProps.gameId}
            playlistId={settingOverrideProps.playlistId ?? 0}
            onConfirm={onConfirmGameSettingsChanges}
            onCancel={() => {
              setSettingOverrideProps({
                playlistIdx: -1,
                playlistId: undefined,
                gameId: undefined
              });
            }}
          />
        )}
    </>
  );
});

const EventFormPage = () => {
  const { id } = useParams();
  const [pageTitle, setPageTitle] = React.useState('');

  return (
    <PrivatePage
      breadcrumb={[
        {
          label: 'Dashboard',
          href: 'dashboard'
        },
        {
          label: 'My events',
          href: 'myevents'
        },
        {
          label: id ? 'Event details' : 'New event',
          href: `myevents/${id ? `edit/${id}` : 'new'}`
        }
      ]}
      pageTitle={pageTitle}>
      <div className="d-flex justify-content-end mb-4">
        <HelpSupport
          title="Creating events"
          url="https://forum.catalystglobal.com/t/4212"
        />
      </div>

      <EventForm setPageTitle={setPageTitle} />
    </PrivatePage>
  );
};

export default EventFormPage;
